import { FC, useEffect, useMemo, useState } from "react";
import {
  Table,
  TableRow,
  TableBody,
  TableCell,
  Button,
  Checkbox,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
  Skeleton,
} from "@capacit-com/lara-law-shared-ui";
import {
  FolderOpen,
  MoreVertical,
  Search,
  Trash2,
  UserPlus,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useOpenAppNavigation } from "@/hooks/useOpenAppNavigation";
import AppRoutes from "@/constants/routes/AppRoutes";
import { workspaceReturnState } from "@/lib/navigation/workspaceReturn";
import { cn } from "@/lib/utils";
import { DataTablePagination } from "@/components/data-table/data-table-pagination";
import { useUserManagementContext } from "@/providers/user-management/UserManagementContext";
import { SortableTableHeader } from "./SortableTableHeader";
import { ORPHANED_WORKSPACES_LAYOUT } from "../utils/tableColumnWidths";
import {
  UserManagementSkeletonRows,
  UserManagementTableHeader,
} from "./UserManagementTableColumns";
import {
  maxColumnSpan,
  type UserManagementColumn,
} from "../utils/userManagementColumns";
import {
  REFRESHING_ROWS_CLASS,
  tableLoadingState,
} from "../utils/tableLoadingState";
import useWorkspace from "@/hooks/useWorkspace";
import Workspace, {
  getAllWorkspaceDocuments,
} from "@/models/workspace/Workspace";
import {
  formatSelectedWorkspaceCount,
  selectAllWorkspaces,
  toggleWorkspaceSelection,
} from "../utils/orphanedWorkspacesSelection";
import BulkClaimOrphanedWorkspacesDialog from "./BulkClaimOrphanedWorkspacesDialog";
import BulkDeleteOrphanedWorkspacesDialog from "./BulkDeleteOrphanedWorkspacesDialog";
import ClaimWorkspaceDialog from "./ClaimWorkspaceDialog";
import DeleteOrphanedWorkspaceDialog from "./DeleteOrphanedWorkspaceDialog";
import UserManagementTableActions from "./UserManagementTableActions";
import { UserManagementTabs } from "../utils/userManagementTabs";

const DEFAULT_PAGE_SIZE = 20;

/** Gives an opened workspace its button back to this list. */
const backToOrphanedWorkspaces = workspaceReturnState(
  AppRoutes.settingsUserManagementOrphanedWorkspaces,
  "Tilbage til forældreløse workspaces"
);

const OrphanedWorkspacesTable: FC = () => {
  const { openNavigation } = useOpenAppNavigation();
  const { useQueryOrphanedWorkspaces, claimWorkspace } = useWorkspace();

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const {
    orphanedWorkspacesSearch: searchQuery,
    setOrphanedWorkspacesSearch: setSearchQuery,
    orphanedWorkspacesSortBy,
    orphanedWorkspacesSortAscending,
    setOrphanedWorkspacesSort,
    bulkMode,
    setBulkMode,
  } = useUserManagementContext();
  const [searchInputValue, setSearchInputValue] = useState(searchQuery);
  const [selectedWorkspaces, setSelectedWorkspaces] = useState<Set<string>>(
    new Set()
  );
  const [claimDialogOpen, setClaimDialogOpen] = useState(false);
  const [workspaceToClaim, setWorkspaceToClaim] = useState<Workspace | null>(
    null
  );
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [workspaceToDelete, setWorkspaceToDelete] = useState<Workspace | null>(
    null
  );
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkClaimDialogOpen, setBulkClaimDialogOpen] = useState(false);

  const apiPageNumber = pageIndex + 1;
  const workspacesQuery = useQueryOrphanedWorkspaces(
    apiPageNumber,
    pageSize,
    searchQuery || undefined,
    orphanedWorkspacesSortBy,
    orphanedWorkspacesSortAscending
  );
  const { data } = workspacesQuery;
  const { showSkeleton: isLoading, isRefreshing } =
    tableLoadingState(workspacesQuery);

  const workspaces = useMemo(() => data?.data ?? [], [data?.data]);

  const totalPages = data?.totalPages ?? 0;
  const pageCount = Math.max(totalPages, 1);

  const hasSearchQuery = searchQuery.trim().length > 0;

  useEffect(() => {
    // If the dataset shrinks and the current page is now out of range, snap back.
    if (totalPages === 0) {
      if (pageIndex !== 0) setPageIndex(0);
      return;
    }

    if (pageIndex > totalPages - 1) {
      setPageIndex(totalPages - 1);
    }
  }, [pageIndex, totalPages]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const trimmed = searchInputValue.trim();
      if (trimmed !== searchQuery) {
        setSearchQuery(trimmed);
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchInputValue, searchQuery, setSearchQuery]);

  useEffect(() => {
    setPageIndex(0);
    setSelectedWorkspaces(new Set());
  }, [searchQuery, orphanedWorkspacesSortBy, orphanedWorkspacesSortAscending]);

  useEffect(() => {
    setSelectedWorkspaces(new Set());
  }, [pageIndex, pageSize]);

  // The page header owns the bulk-mode flag; the selection it applies to is ours to clear.
  useEffect(() => {
    setSelectedWorkspaces(new Set());
  }, [bulkMode]);

  const handleSearch = () => {
    setSearchQuery(searchInputValue.trim());
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleBulkSelect = (workspaceId: string, checked: boolean) => {
    setSelectedWorkspaces((prev) =>
      toggleWorkspaceSelection(prev, workspaceId, checked)
    );
  };

  const handleSelectAll = (checked: boolean) => {
    setSelectedWorkspaces(
      selectAllWorkspaces(
        workspaces.map((w) => w.id),
        checked
      )
    );
  };

  const columns: UserManagementColumn[] = [
    {
      key: "select",
      width: ORPHANED_WORKSPACES_LAYOUT.columns.select,
      hidden: !bulkMode,
      header: (
        <Checkbox
          checked={
            workspaces.length > 0 &&
            workspaces.every((w) => selectedWorkspaces.has(w.id))
          }
          onCheckedChange={handleSelectAll}
          disabled={isLoading || workspaces.length === 0}
        />
      ),
      skeleton: <Skeleton className="h-4 w-4" />,
    },
    {
      key: "name",
      width: ORPHANED_WORKSPACES_LAYOUT.columns.name,
      header: (
        <SortableTableHeader
          label="Navn"
          sortBy="Name"
          activeSortBy={orphanedWorkspacesSortBy}
          ascending={orphanedWorkspacesSortAscending}
          onSort={setOrphanedWorkspacesSort}
        />
      ),
      skeleton: <Skeleton className="h-4 w-full max-w-32" />,
    },
    {
      key: "description",
      width: ORPHANED_WORKSPACES_LAYOUT.columns.description,
      header: "Beskrivelse",
      skeleton: <Skeleton className="h-4 w-full max-w-48" />,
    },
    {
      key: "documentCount",
      width: ORPHANED_WORKSPACES_LAYOUT.columns.documentCount,
      align: "center",
      header: "Dokumenter",
      skeleton: <Skeleton className="h-4 w-8 mx-auto" />,
    },
    {
      key: "createdAt",
      width: ORPHANED_WORKSPACES_LAYOUT.columns.createdAt,
      header: (
        <SortableTableHeader
          label="Oprettet"
          sortBy="CreatedAt"
          activeSortBy={orphanedWorkspacesSortBy}
          ascending={orphanedWorkspacesSortAscending}
          onSort={setOrphanedWorkspacesSort}
        />
      ),
      skeleton: <Skeleton className="h-4 w-full max-w-24" />,
    },
    {
      key: "actions",
      width: ORPHANED_WORKSPACES_LAYOUT.columns.actions,
      align: "right",
      header: "Handlinger",
      skeleton: <Skeleton className="h-8 w-24 ml-auto" />,
    },
  ];
  const colSpan = maxColumnSpan(columns);

  const handleClaimClick = (workspace: Workspace) => {
    setWorkspaceToClaim(workspace);
    setClaimDialogOpen(true);
  };

  const handleDeleteClick = (workspace: Workspace) => {
    setWorkspaceToDelete(workspace);
    setDeleteDialogOpen(true);
  };

  const handleOpenWorkspace = (
    workspace: Workspace,
    event?: React.MouseEvent
  ) => {
    openNavigation(
      { kind: "workspaceFiles", workspaceId: workspace.id },
      event,
      {
        navigateOptions: { state: backToOrphanedWorkspaces },
      }
    );
  };

  const formatDate = (dateString: string | Date) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("da-DK", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const emptyMessage = hasSearchQuery
    ? "Ingen workspaces matcher søgningen"
    : "Der er ingen forældreløse workspaces.";

  const selectedList = useMemo(
    () => workspaces.filter((w) => selectedWorkspaces.has(w.id)),
    [workspaces, selectedWorkspaces]
  );

  return (
    <div className="flex flex-col h-full gap-4 min-h-0">
      <div className="flex items-center justify-between gap-4 min-h-9">
        <div className="relative w-full max-w-sm">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Søg efter workspaces..."
            className="pl-8 pr-10"
            value={searchInputValue}
            onChange={(e) => setSearchInputValue(e.target.value)}
            onKeyDown={handleSearchKeyDown}
          />
          <Button
            size="icon"
            variant="ghost"
            className="absolute right-0 top-0 h-full px-2"
            onClick={handleSearch}
            disabled={isLoading}
            aria-label="Søg"
          >
            <Search className="h-4 w-4" />
          </Button>
        </div>
        <UserManagementTableActions
          tab={UserManagementTabs.ORPHANED_WORKSPACES}
        />
      </div>

      {bulkMode && (
        <div
          className={cn(
            "flex items-center justify-between p-3 bg-primary/10 rounded-md",
            selectedWorkspaces.size === 0 && "opacity-50"
          )}
        >
          <span className="text-sm font-medium">
            {formatSelectedWorkspaceCount(selectedWorkspaces.size)}
          </span>
          <div className="flex gap-2">
            <Button
              disabled={
                selectedWorkspaces.size === 0 || claimWorkspace.isPending
              }
              size="sm"
              variant="outline"
              onClick={() => setBulkClaimDialogOpen(true)}
            >
              <UserPlus className="w-4 h-4 mr-1" />
              Tildel ejer til valgte
            </Button>
            <Button
              disabled={selectedWorkspaces.size === 0}
              size="sm"
              variant="outline"
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={() => setBulkDeleteDialogOpen(true)}
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Slet valgte workspaces
            </Button>
          </div>
        </div>
      )}

      <div className="flex-1 min-h-0 flex flex-col">
        <div className="flex-1 min-h-0 overflow-y-auto border rounded-xs">
          <Table
            removeOverflowX={true}
            className="table-fixed"
            style={{ minWidth: ORPHANED_WORKSPACES_LAYOUT.minWidth }}
            aria-busy={isRefreshing}
          >
            <UserManagementTableHeader columns={columns} />
            <TableBody className={cn(isRefreshing && REFRESHING_ROWS_CLASS)}>
              {isLoading ? (
                <UserManagementSkeletonRows columns={columns} rows={5} />
              ) : workspaces.length > 0 ? (
                workspaces.map((workspace) => (
                  <TableRow key={workspace.id}>
                    {bulkMode && (
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedWorkspaces.has(workspace.id)}
                          onCheckedChange={(checked) =>
                            handleBulkSelect(workspace.id, checked as boolean)
                          }
                        />
                      </TableCell>
                    )}
                    <TableCell className="font-medium truncate">
                      {bulkMode ? (
                        workspace.name
                      ) : (
                        <Link
                          to={AppRoutes.workspaceFiles(workspace.id)}
                          state={backToOrphanedWorkspaces}
                          className="hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
                        >
                          {workspace.name}
                        </Link>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground truncate">
                      {workspace.description || "Ingen beskrivelse"}
                    </TableCell>
                    <TableCell className="text-center">
                      {getAllWorkspaceDocuments(workspace).length}
                    </TableCell>
                    <TableCell>
                      {workspace.createdAt
                        ? formatDate(workspace.createdAt)
                        : "-"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => handleOpenWorkspace(workspace, e)}
                          disabled={bulkMode}
                          aria-label={`Åbn ${workspace.name}`}
                        >
                          <FolderOpen className="w-4 h-4 mr-1" />
                          Åbn
                        </Button>
                        <DropdownMenu modal={false}>
                          <DropdownMenuTrigger asChild>
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={bulkMode || claimWorkspace.isPending}
                              aria-label={`Handlinger for ${workspace.name}`}
                            >
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => handleClaimClick(workspace)}
                            >
                              <UserPlus className="w-4 h-4 mr-2" />
                              Tildel ejer
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => handleDeleteClick(workspace)}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Slet workspace
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={colSpan} className="text-center py-8">
                    <p className="text-muted-foreground">{emptyMessage}</p>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <DataTablePagination
        pageIndex={pageIndex}
        pageSize={pageSize}
        pageCount={pageCount}
        setPageIndex={setPageIndex}
        setPageSize={setPageSize}
        pageSizeOptions={[20, 40, 60, 80, 100]}
      />

      <ClaimWorkspaceDialog
        isOpen={claimDialogOpen}
        onClose={() => {
          setClaimDialogOpen(false);
          setWorkspaceToClaim(null);
        }}
        workspace={workspaceToClaim}
      />

      <DeleteOrphanedWorkspaceDialog
        isOpen={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setWorkspaceToDelete(null);
        }}
        workspace={workspaceToDelete}
      />

      <BulkClaimOrphanedWorkspacesDialog
        isOpen={bulkClaimDialogOpen}
        onClose={() => setBulkClaimDialogOpen(false)}
        workspaces={selectedList}
        onClaimSuccess={() => {
          setSelectedWorkspaces(new Set());
          setBulkMode(false);
        }}
        onPartialClaim={(succeededIds) => {
          setSelectedWorkspaces((prev) => {
            const next = new Set(prev);
            for (const id of succeededIds) {
              next.delete(id);
            }
            return next;
          });
        }}
      />

      <BulkDeleteOrphanedWorkspacesDialog
        isOpen={bulkDeleteDialogOpen}
        onClose={() => setBulkDeleteDialogOpen(false)}
        workspaces={selectedList}
        onDeleteSuccess={() => {
          setSelectedWorkspaces(new Set());
          setBulkMode(false);
        }}
        onPartialDelete={(succeededIds) => {
          setSelectedWorkspaces((prev) => {
            const next = new Set(prev);
            for (const id of succeededIds) {
              next.delete(id);
            }
            return next;
          });
        }}
      />
    </div>
  );
};

export default OrphanedWorkspacesTable;
